import React, { useState, useEffect, useCallback } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Autocomplete,
  Chip,
  Paper,
  Stack
} from '@mui/material';
import { debounce } from 'lodash';
import api from '../../../services/api';
import {
  CaseEditorStepFrame,
  caseManagerInsetSurfaceSx,
  caseManagerPalette,
  caseManagerSelectSx,
  publicBodyFontFamily,
  publicTextFieldSx,
} from '../caseManagerStyles';

const PatientDetailsStep = ({ data, presets, onUpdate, validationErrors = [], allowNewSpecialty = false }) => {
  const [titleError, setTitleError] = useState('');
  const [checkingTitle, setCheckingTitle] = useState(false);

  // Helper function to check if a field has validation errors
  const hasFieldError = (fieldName) => {
    return validationErrors.some(error => 
      error.toLowerCase().includes(fieldName.toLowerCase())
    );
  };

  // Helper function for case-insensitive preset matching
  const findExistingPreset = (userInput, presetArray, key = 'name') => {
    if (!userInput || !Array.isArray(presetArray)) return null;
    
    const trimmedInput = userInput.trim();
    return presetArray.find(preset => 
      preset[key].toLowerCase() === trimmedInput.toLowerCase()
    );
  };

  // Set defaults on first load
  useEffect(() => {
    if (!data.gender) {
      onUpdate({ gender: 'Male' });
    }
    if (!data.difficulty) {
      onUpdate({ difficulty: 'Core' });
    }
  }, []);

  const calcAge = (d) => {
    const today = new Date();
    let a = today.getFullYear() - d.getFullYear();
    const m = today.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < d.getDate())) a--;
    return a;
  };

  const handleDateOfBirthChange = (raw) => {
    // Keep only digits
    let digits = raw.replace(/\D/g, '').slice(0, 8); // DDMMYYYY max

    // Auto add slashes after 2nd and 4th digits
    if (digits.length > 4) {
      digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    } else if (digits.length > 2) {
      digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    }

    onUpdate({ dateOfBirth: digits });

    // Validate when full length reached
    if (digits.length === 10) {
      const [dd, mm, yyyy] = digits.split('/').map(Number);
      const date = new Date(yyyy, mm - 1, dd);
      const valid =
        date &&
        date.getFullYear() === yyyy &&
        date.getMonth() === mm - 1 &&
        date.getDate() === dd &&
        yyyy >= 1900 &&
        yyyy <= new Date().getFullYear();

      if (!valid) {
        onUpdate({ age: '' });
        return;
      }
      const age = calcAge(date);
      if (age < 0 || age > 150) {
        onUpdate({ age: '' });
        return;
      }

      onUpdate({ age: age.toString() });
    } else {
      onUpdate({ age: '' });
    }
  };

  const checkTitleUniqueness = async (title, currentId = null) => {
    if (!title.trim()) {
      setTitleError('');
      return;
    }

    setCheckingTitle(true);
    try {
      const params = new URLSearchParams({ title });
      if (currentId) {
        params.append('id', currentId);
      }
      
      const response = await api.get(`/cases/check-title?${params}`);
      
      if (!response.data.available) {
        setTitleError('A case with this title already exists');
      } else {
        setTitleError('');
      }
    } catch (error) {
      console.error('Error checking title uniqueness:', error);
    } finally {
      setCheckingTitle(false);
    }
  };

  // Debounced title check
  const debouncedTitleCheck = useCallback(
    debounce((title, currentId) => checkTitleUniqueness(title, currentId), 500),
    []
  );

  const handleTitleChange = (value) => {
    onUpdate({ title: value });
    debouncedTitleCheck(value, data.id);
  };

  const applyPresetValue = (field, value, presetArray, newFlag, allowNew = true) => {
    if (!value) {
      onUpdate({ [field]: '', [newFlag]: false });
      return;
    }

    const trimmedValue = typeof value === 'string'
      ? value.trim()
      : (value?.name || '').trim();

    if (!trimmedValue) {
      onUpdate({ [field]: '', [newFlag]: false });
      return;
    }

    const existingPreset = findExistingPreset(trimmedValue, presetArray);

    if (existingPreset) {
      onUpdate({
        [field]: existingPreset.name,
        [newFlag]: false
      });
    } else if (allowNew) {
      const capitalizedValue = trimmedValue.charAt(0).toUpperCase() + trimmedValue.slice(1);
      onUpdate({
        [field]: capitalizedValue,
        [newFlag]: true
      });
    } else {
      onUpdate({ [field]: '', [newFlag]: false });
    }
  };

  const handlePresentingComplaintChange = (event, value) => {
    applyPresetValue('presentingComplaint', value, presets.presentingComplaints, '_newPresentingComplaint');
  };

  const handlePresentingComplaintInput = (event, newInputValue, reason) => {
    if (reason === 'input') {
      applyPresetValue('presentingComplaint', newInputValue, presets.presentingComplaints, '_newPresentingComplaint');
    }
    if (reason === 'clear') {
      applyPresetValue('presentingComplaint', '', presets.presentingComplaints, '_newPresentingComplaint');
    }
  };

  const handleSpecialtyChange = (event, value) => {
    applyPresetValue('specialty', value, presets.specialties, '_newSpecialty', allowNewSpecialty);
  };

  const handleSpecialtyInput = (event, newInputValue, reason) => {
    if (!allowNewSpecialty) {
      if (reason === 'clear') {
        applyPresetValue('specialty', '', presets.specialties, '_newSpecialty', false);
      }
      return;
    }
    if (reason === 'input') {
      applyPresetValue('specialty', newInputValue, presets.specialties, '_newSpecialty', true);
    }
    if (reason === 'clear') {
      applyPresetValue('specialty', '', presets.specialties, '_newSpecialty', true);
    }
  };

  const normalizeTags = (value) => {
    if (!Array.isArray(value)) {
      return [];
    }

    const normalized = value
      .map((tag) => (typeof tag === 'string' ? tag.trim() : tag?.name || '').trim())
      .filter(Boolean);

    return Array.from(new Set(normalized)).slice(0, 30);
  };

  const handleTagsChange = (_event, value) => {
    onUpdate({ tags: normalizeTags(value) });
  };

  const fieldSx = {
    ...publicTextFieldSx,
    '& .MuiOutlinedInput-root.Mui-error fieldset': {
      borderColor: '#f44336',
      borderWidth: 2,
    },
  };

  return (
    <CaseEditorStepFrame>
      <Stack spacing={2.2}>
        <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 2, md: 2.4 } }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ lg: 'center' }}>
            <Box>
              <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                Start with a strong case heading
              </Typography>
              <Typography sx={{ mt: 0.6, color: caseManagerPalette.inkSoft, lineHeight: 1.68, fontFamily: publicBodyFontFamily }}>
                Use a clear title, presenting complaint, and specialty so the case is easy to find later and straightforward for staff to review.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${Array.isArray(data.tags) ? data.tags.length : 0} tags`} sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
              <Chip label={checkingTitle ? 'Checking title…' : 'Title ready'} sx={{ fontFamily: publicBodyFontFamily, fontWeight: 700 }} />
            </Stack>
          </Stack>
        </Paper>

        <Grid container spacing={2.2}>
          <Grid item xs={12} lg={7}>
            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 2, md: 2.4 }, height: '100%' }}>
              <Stack spacing={2.1}>
                <Box>
                  <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                    Case identity
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: caseManagerPalette.inkSoft, fontSize: '0.95rem', lineHeight: 1.65, fontFamily: publicBodyFontFamily }}>
                    Define the teaching context and the clinical frame for this case.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Case Title"
                  value={data.title || ''}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  required
                  error={!!titleError || hasFieldError('case title')}
                  helperText={titleError || (hasFieldError('case title') ? 'Case title is required' : 'A descriptive title for this case')}
                  sx={fieldSx}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      freeSolo
                      options={presets.presentingComplaints}
                      value={data.presentingComplaint || ''}
                      onChange={handlePresentingComplaintChange}
                      onInputChange={handlePresentingComplaintInput}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option?.name || ''
                      }
                      isOptionEqualToValue={(option, value) => {
                        const optionName = typeof option === 'string' ? option : option?.name;
                        const valueName = typeof value === 'string' ? value : value?.name;
                        return optionName?.toLowerCase?.() === valueName?.toLowerCase?.();
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>{typeof option === 'string' ? option : option.name}</li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Presenting Complaint"
                          required
                          error={hasFieldError('presenting complaint')}
                          helperText={
                            hasFieldError('presenting complaint')
                              ? 'Presenting complaint is required'
                              : data._newPresentingComplaint
                                ? 'New presenting complaint will be added when you save'
                                : 'Select from the library or type a new complaint'
                          }
                          sx={fieldSx}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Autocomplete
                      freeSolo={allowNewSpecialty}
                      options={presets.specialties}
                      value={data.specialty || ''}
                      onChange={handleSpecialtyChange}
                      onInputChange={handleSpecialtyInput}
                      getOptionLabel={(option) =>
                        typeof option === 'string' ? option : option?.name || ''
                      }
                      isOptionEqualToValue={(option, value) => {
                        const optionName = typeof option === 'string' ? option : option?.name;
                        const valueName = typeof value === 'string' ? value : value?.name;
                        return optionName?.toLowerCase?.() === valueName?.toLowerCase?.();
                      }}
                      renderOption={(props, option) => (
                        <li {...props}>{typeof option === 'string' ? option : option.name}</li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Specialty"
                          required
                          error={hasFieldError('specialty')}
                          helperText={
                            hasFieldError('specialty')
                              ? 'Specialty is required'
                              : allowNewSpecialty
                                ? (data._newSpecialty
                                  ? 'New specialty will be created when you save'
                                  : 'Select an existing specialty or add a new one')
                                : 'Select from the approved specialty list'
                          }
                          sx={fieldSx}
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <Autocomplete
                  multiple
                  freeSolo
                  options={Array.isArray(presets.tags) ? presets.tags : []}
                  value={Array.isArray(data.tags) ? data.tags : []}
                  onChange={handleTagsChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tags"
                      helperText="Add tags to improve search and organisation, for example GI, acute abdomen, or red flags."
                      sx={fieldSx}
                    />
                  )}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Short Description"
                  value={data.shortDescription || ''}
                  onChange={(e) => onUpdate({ shortDescription: e.target.value })}
                  helperText={`Short case summary for lists and previews (${(data.shortDescription || '').length}/250 characters)`}
                  inputProps={{ maxLength: 250 }}
                  sx={fieldSx}
                />
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} lg={5}>
            <Paper sx={{ ...caseManagerInsetSurfaceSx, p: { xs: 2, md: 2.4 }, height: '100%' }}>
              <Stack spacing={2.1}>
                <Box>
                  <Typography sx={{ color: caseManagerPalette.ink, fontWeight: 800, fontFamily: publicBodyFontFamily }}>
                    Patient profile
                  </Typography>
                  <Typography sx={{ mt: 0.5, color: caseManagerPalette.inkSoft, fontSize: '0.95rem', lineHeight: 1.65, fontFamily: publicBodyFontFamily }}>
                    Keep this realistic and internally consistent so the patient details match the narrative and the chosen character.
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Patient Name"
                      value={data.patientName || ''}
                      onChange={(e) => onUpdate({ patientName: e.target.value })}
                      required
                      error={hasFieldError('patient name')}
                      helperText={hasFieldError('patient name') ? 'Patient name is required' : 'Use the name students will hear in the consultation'}
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={hasFieldError('gender')} sx={caseManagerSelectSx}>
                      <InputLabel>Gender</InputLabel>
                      <Select
                        value={data.gender || 'Male'}
                        label="Gender"
                        onChange={(e) => onUpdate({ gender: e.target.value })}
                      >
                        <MenuItem value="Male">Male</MenuItem>
                        <MenuItem value="Female">Female</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                    {hasFieldError('gender') && (
                      <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.75, display: 'block', fontFamily: publicBodyFontFamily }}>
                        Gender is required
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required error={hasFieldError('difficulty')} sx={caseManagerSelectSx}>
                      <InputLabel>Difficulty Level</InputLabel>
                      <Select
                        value={data.difficulty || 'Core'}
                        label="Difficulty Level"
                        onChange={(e) => onUpdate({ difficulty: e.target.value })}
                      >
                        <MenuItem value="Core">Core</MenuItem>
                        <MenuItem value="Intermediate">Intermediate</MenuItem>
                        <MenuItem value="Advanced">Advanced</MenuItem>
                      </Select>
                    </FormControl>
                    {hasFieldError('difficulty') && (
                      <Typography variant="caption" color="error" sx={{ ml: 1, mt: 0.75, display: 'block', fontFamily: publicBodyFontFamily }}>
                        Difficulty level is required
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      value={data.dateOfBirth || ''}
                      onChange={(e) => handleDateOfBirthChange(e.target.value)}
                      placeholder="DD/MM/YYYY"
                      required
                      error={hasFieldError('date of birth')}
                      helperText={
                        hasFieldError('date of birth')
                          ? 'Date of birth is required in DD/MM/YYYY format'
                          : 'Enter date in DD/MM/YYYY format'
                      }
                      inputProps={{
                        pattern: '[0-9]{2}/[0-9]{2}/[0-9]{4}',
                        maxLength: 10
                      }}
                      sx={fieldSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Age"
                      value={data.age || ''}
                      disabled
                      error={hasFieldError('age')}
                      helperText={
                        hasFieldError('age')
                          ? 'Valid age is required and is calculated from the date of birth'
                          : 'Calculated automatically from the date of birth'
                      }
                      sx={fieldSx}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </CaseEditorStepFrame>
  );
};

export default PatientDetailsStep;

